import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import useTranslation from '../../customHooks/translations';
import FishingRadarMap from './FishingRadarMap';

const RADAR_URL = process.env.REACT_APP_FISHING_RADAR_URL;

export default function FishingRadarDialog({ open, onClose }) {
  const t = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [state, setState] = useState({ status: 'idle', data: null });
  const startedRef = useRef(false);

  useEffect(() => {
    if (!open) { startedRef.current = false; return undefined; }
    if (startedRef.current) return undefined;
    startedRef.current = true;
    let alive = true;
    setState({ status: 'loading', data: null });
    fetch(RADAR_URL, { cache: 'no-store' })
      .then((r) => { if (!r.ok) throw new Error('http'); return r.json(); })
      .then((data) => { if (alive) setState({ status: 'loaded', data }); })
      .catch(() => { if (alive) setState({ status: 'error', data: null }); });
    return () => { alive = false; };
  }, [open]);

  const body = (() => {
    if (state.status === 'loading' || state.status === 'idle') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
          <CircularProgress size={22} /> <Typography>{t.fishingRadar.loading}</Typography>
        </Box>
      );
    }
    if (state.status === 'error') {
      return <Box sx={{ p: 3 }}><Typography>{t.fishingRadar.error}</Typography></Box>;
    }
    const regions = (state.data && state.data.regions) || [];
    if (!regions.length) return <Typography sx={{ p: 3 }}>{t.fishingRadar.empty}</Typography>;
    return (
      <Box sx={{ p: 2 }}>
        <FishingRadarMap regions={regions} scoredAt={state.data.scoredAt} />
      </Box>
    );
  })();

  const scoredAt = state.status === 'loaded' && state.data ? state.data.scoredAt : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="md">
      <AppBar
        sx={{ position: 'relative', pt: fullScreen ? 'env(safe-area-inset-top)' : undefined }}
        color="default"
        elevation={0}
      >
        <Toolbar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>{t.fishingRadar.title}</Typography>
            {scoredAt && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                {`${t.fishingRadar.updated}: ${new Date(scoredAt).toLocaleString()}`}
              </Typography>
            )}
          </Box>
          <IconButton edge="end" onClick={onClose} aria-label={t.fishingRadar.close}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      {body}
    </Dialog>
  );
}
